"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Cpu, Zap } from "lucide-react";

/**
 * 3D-Platinen-Raster + Laser-Leiterbahnen im Hintergrund,
 * gestochen scharfer Silizium-Chip im Vordergrund!
 */
export default function Preview() {
  const [turbo, setTurbo] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setTurbo((v) => !v), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{
          scale: turbo ? [1, 1.3, 1.15] : 0.9,
          opacity: turbo ? 0.9 : 0.45,
        }}
        transition={{ duration: 0.9 }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-tr from-purple-600/35 via-fuchsia-500/25 to-emerald-400/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.32)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.32)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex items-center justify-center antialiased">
        <div className="pointer-events-none absolute flex w-60 items-center justify-between">
          <motion.div
            animate={{ x: [-20, 30], opacity: [0, 1, 0] }}
            transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
            className="h-1 w-12 rounded-full bg-gradient-to-r from-transparent via-purple-400 to-emerald-400 shadow-[0_0_12px_#10b981]"
          />
          <motion.div
            animate={{ x: [20, -30], opacity: [0, 1, 0] }}
            transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
            className="h-1 w-12 rounded-full bg-gradient-to-l from-transparent via-purple-400 to-emerald-400 shadow-[0_0_12px_#10b981]"
          />
        </div>

        <motion.div
          animate={{ y: turbo ? -3 : 2 }}
          transition={{ type: "spring", stiffness: 240, damping: 20 }}
          className="relative flex h-28 w-44 flex-col items-center justify-center rounded-2xl border border-purple-400/70 bg-white p-3 shadow-[0_20px_40px_-8px_rgba(168,85,247,0.55)] dark:bg-zinc-900"
        >
          <div className="absolute -top-1.5 flex gap-3">
            {[0, 1, 2, 3].map((p) => (
              <span
                key={p}
                className="h-1.5 w-2.5 rounded-xs bg-amber-400 shadow-[0_0_6px_#f59e0b]"
              />
            ))}
          </div>
          <div className="absolute -bottom-1.5 flex gap-3">
            {[0, 1, 2, 3].map((p) => (
              <span
                key={p}
                className="h-1.5 w-2.5 rounded-xs bg-amber-400 shadow-[0_0_6px_#f59e0b]"
              />
            ))}
          </div>

          <div className="flex flex-col items-center gap-1 rounded-xl border border-emerald-400/80 bg-gradient-to-br from-purple-700 via-indigo-900 to-emerald-900 px-4 py-2 text-white shadow-[0_10px_25px_rgba(16,185,129,0.5)]">
            <div className="flex items-center gap-1.5">
              <Cpu className="h-4 w-4 text-emerald-400" />
              <span className="font-mono text-xs font-black tracking-wider">
                WebNN NPU
              </span>
            </div>
            <span className="flex items-center gap-1 font-mono text-[10px] font-bold text-emerald-300">
              <Zap className="h-3 w-3 fill-amber-400 text-amber-400" />
              {turbo ? "60 FPS · 1.2W" : "DirectML / GPU"}
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
